import { ReactNode } from 'react';

import { MenuNode } from 'components/header/menu-modules/types';
import { PermissionKey } from 'context/PermissionsContext';

interface RouteMenuHandle {
    title: string;
    icon?: ReactNode;
    permissions?: PermissionKey[];
    group?: number;
}

type RouteNode = {
    path?: string;
    handle?: { menu?: RouteMenuHandle };
    children?: RouteNode[];
};

/**
 * Constrói a árvore de menus recursivamente a partir das rotas.
 * Valida as permissões de cada menu e exibe apenas os que o usuário tem permissão.
 */
export function buildMenuTree(routes: RouteNode[], basePath = '', hasAnyPermission: (perms: PermissionKey[]) => boolean) {
    const nodes: MenuNode[] = [];

    for (const route of routes) {
        const menu = route.handle?.menu;

        if (!menu) continue;

        const fullPath = route.path ? `${basePath}/${route.path}` : basePath;
        const children = route.children ? buildMenuTree(route.children, fullPath, hasAnyPermission) : [];
        const userHasPermission = !!menu.permissions?.length && hasAnyPermission(menu.permissions);
        const isVisible = userHasPermission || children.length > 0;

        if (!isVisible) continue;

        nodes.push({
            title: menu.title,
            icon: menu.icon,
            path: children.length ? undefined : fullPath,
            permissions: menu.permissions,
            group: menu.group,
            children,
        });
    }

    return nodes;
}

/**
 * Retorna uma lista flat (sem hierarquia) de menus a partir da árvore de menus.
 * Cada menu contém o caminho completo, o ícone e o breadcrumb.
 */
export function flattenMenuTree(nodes: MenuNode[], ancestors: string[] = [], inheritedIcon?: ReactNode) {
    const result: MenuNode[] = [];
    for (const node of nodes) {
        const icon = node.icon ?? inheritedIcon;
        if (node.children && node.children.length > 0) {
            result.push(...flattenMenuTree(node.children, [...ancestors, node.title], icon));
        } else if (node.path) {
            result.push({
                title: node.title,
                path: node.path,
                icon,
                breadcrumb: ancestors.length > 0 ? ancestors.join(' › ') : undefined,
            });
        }
    }
    return result;
}
